import {
  AcceptInvitationPayload,
  AcceptInvitationResponse,
  InvitePayload,
  KnownTeammate,
  TeamInvitation,
  TeamMember,
  TeamResponse,
} from './models/team';
import Service from './Service';

// Per-location team management. `locationId` is kept as a string|number so the
// route param can be passed straight through without a conversion dance.
class TeamService {
  private static URL_BASE = process.env.REACT_APP_URL_API;

  private static URL_TEAM = (locationId: string | number) => `${this.URL_BASE}/admin/locations/${locationId}/team`;
  private static URL_MEMBER = (locationId: string | number, memberId: number) => `${this.URL_TEAM(locationId)}/${memberId}`;
  private static URL_RESEND = (locationId: string | number, memberId: number) => `${this.URL_MEMBER(locationId, memberId)}/resend`;
  private static URL_KNOWN_MEMBERS = () => `${this.URL_BASE}/admin/team/known-members`;
  private static URL_INVITATION = (token: string) => `${this.URL_BASE}/v1/auth/invitations/${token}`;
  private static URL_ACCEPT_INVITATION = (token: string) => `${this.URL_INVITATION(token)}/accept`;

  static getTeam(locationId: string | number): Promise<TeamResponse> {
    return Service.get(this.URL_TEAM(locationId));
  }

  static invite(locationId: string | number, payload: InvitePayload): Promise<{ member: TeamMember }> {
    return Service.post(this.URL_TEAM(locationId), payload);
  }

  // 204 — the email is sent fire-and-forget server side.
  static resendInvite(locationId: string | number, memberId: number): Promise<void> {
    return Service.post(this.URL_RESEND(locationId, memberId));
  }

  static updateMember(
    locationId: string | number,
    memberId: number,
    rentalEnabled: boolean,
  ): Promise<{ member: TeamMember }> {
    return Service.patch(this.URL_MEMBER(locationId, memberId), { rentalEnabled });
  }

  // Removes an active member or revokes a pending invite. 204.
  static removeMember(locationId: string | number, memberId: number): Promise<void> {
    return Service.delete(this.URL_MEMBER(locationId, memberId));
  }

  static getKnownTeammates(options?: {
    search?: string;
    excludeLocationId?: string | number;
    limit?: number;
  }): Promise<{ members: KnownTeammate[] }> {
    const params = new URLSearchParams();
    // The API rejects a 1-character search, so only send a usable one.
    if (options?.search && options.search.length >= 2) params.set('search', options.search);
    if (options?.excludeLocationId != null) params.set('exclude_location_id', String(options.excludeLocationId));
    if (options?.limit != null) params.set('limit', String(options.limit));
    const query = params.toString();
    return Service.get(`${this.URL_KNOWN_MEMBERS()}${query ? `?${query}` : ''}`);
  }

  // --- Public invitation endpoints: the emailed token is the credential, so
  // these must not be treated as authenticated calls (a stale/foreign session
  // token in localStorage would otherwise trigger the 401 refresh dance).
  static getInvitation(token: string): Promise<TeamInvitation> {
    return Service.fetch(this.URL_INVITATION(token), { method: 'GET' }, true);
  }

  static acceptInvitation(token: string, payload: AcceptInvitationPayload): Promise<AcceptInvitationResponse> {
    return Service.fetch(this.URL_ACCEPT_INVITATION(token), {
      method: 'POST',
      body: payload,
    }, true);
  }
}

export default TeamService;
